import { useEffect, useState, useRef } from "react";
import dayjs from "dayjs";

/**
 * Custom hook to manage a date that updates exactly once at midnight
 * @param {string} storageKey - localStorage key to persist data
 * @param {Object} initialData - Default data structure if nothing exists in localStorage
 * @returns {Object} - Contains data object, updateDate and updateData functions
 */
const useDateWithMidnightUpdate = (storageKey, initialData = {}) => {
  // Ref to store timeout ID
  const midnightTimeoutRef = useRef(null);

  // Get initial data from localStorage or use provided default
  const getInitialData = () => {
    const today = dayjs().format("YYYY-MM-DD");
    const defaultData = {
      level: "level1",
      activeDay: today,
      lastUpdated: today,
      ...initialData
    };

    try {
      const storedData = localStorage.getItem(storageKey);

      if (!storedData) {
        return defaultData;
      }

      const parsed = JSON.parse(storedData);

      // Check if we need to update to today on initial load
      if (dayjs(parsed.lastUpdated).isBefore(dayjs(today), "day")) {
        localStorage.removeItem("diaryLastVisited");  
        localStorage.setItem("redirectToMod", true)
        return {
          ...parsed,
          activeDay: today,
          lastUpdated: today
        };
      }

      return parsed;
    } catch (error) {
      console.error("Error loading data from localStorage:", error);
      return defaultData;
    }
  };

  const [data, setData] = useState(getInitialData);

  // Function to check if we need to update the date
  const checkForDateUpdate = () => {
    const today = dayjs().format("YYYY-MM-DD");

    // If we haven't updated today (lastUpdated is before today)
    if (dayjs(data.lastUpdated).isBefore(dayjs(today), "day")) {
      setData((prev) => ({
        ...prev,
        activeDay: today,
        lastUpdated: today
      }));
      console.log("Date updated to new day:", today);

      // Reset "lastVisitedDiary" key in localStorage when the day changes
      localStorage.removeItem("diaryLastVisited");
      localStorage.setItem("redirectToMod", true)
    }
  };

  // Save data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  }, [data, storageKey]);

  // Set up the check for date transition
  useEffect(() => {
    // Calculate time until next midnight
    const calculateTimeUntilMidnight = () => {
      const tomorrow = dayjs().add(1, "day").startOf("day");
      return tomorrow.diff(dayjs());
    };

    // Set timeout for next midnight
    const scheduleMidnightUpdate = () => {
      const timeUntilMidnight = calculateTimeUntilMidnight();

      // Clear any existing timeout
      if (midnightTimeoutRef.current) {
        clearTimeout(midnightTimeoutRef.current);
      }

      // Set new timeout for midnight
      midnightTimeoutRef.current = setTimeout(() => {
        checkForDateUpdate();
        // Schedule next update
        scheduleMidnightUpdate();
      }, timeUntilMidnight);

      console.log(
        `Next date check scheduled in ${Math.round(
          timeUntilMidnight / 1000 / 60
        )} minutes`
      );
    };

    // Check immediately in case we loaded after midnight but before update
    checkForDateUpdate();

    // Start the midnight timeout cycle
    scheduleMidnightUpdate();

    // Clean up timeout when component unmounts
    return () => {
      if (midnightTimeoutRef.current) {
        clearTimeout(midnightTimeoutRef.current);
      }
    };
  }, []); // Empty dependency - only run once on component mount

  // Function to manually update active date (preserves lastUpdated)
  const updateDate = (newDate) => {
    setData((prev) => ({
      ...prev,
      activeDay: newDate
      // Don't update lastUpdated here so midnight update still works
    }));
  };

  // Function to update any fields in the data
  const updateData = (updates) => {
    setData((prev) => ({
      ...prev,
      ...updates,
      // Ensure lastUpdated doesn't get overwritten by accident
      lastUpdated: prev.lastUpdated
    }));
  };

  return { data, updateDate, updateData };
};

export default useDateWithMidnightUpdate;
